<template>
  <div
    v-clickoutside="close"
    class="dropdown-wrap"
    :class="{
      open: isOpened,
      error: error,
      'has-flags': showFlags,
      'not-in-modal': !openInModal
    }"
    @keyup.up="selectNext(true)"
    @keyup.down="selectNext(false)"
    @keyup.enter="selectOnEnter"
    @keyup.esc="toggleClosed"
  >
    <modal-header-white
      v-if="isMobile && isOpened && openInModal"
      :device-type="'mobile'"
      :label="mobileTitle"
      @closed="close"
    />
    <span
      class="arrow-down"
      @click="toggleDropdown"
    />
    <div
      v-if="selectedItem && showFlags"
      class="input-flag"
      :class="getFlagClass(selectedItem.split('|')[1])"
    />
    <input
      :value="search"
      class="dropdown-field"
      :class="{ 'has-flag': selectedItem && showFlags }"
      :placeholder="placeholder"
      :autocomplete="dropdownAutocomplete"
      @click="toggleOpened"
      @input="onChange"
    />
    <ul
      :class="['dropdown-list', emptyClass]"
      @click="toggleClosed"
    >
      <li
        v-for="(item, index) in results"
        :key="index"
        :class="{ selected: item.value === selected }"
        @click="selectItem(item.value)"
      >
        <div
          v-if="showFlags && item.key"
          class="flag"
          :class="getFlagClass(item.key)"
        />
        <span>{{ item.title }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import find from 'lodash-es/find'
  import get from 'lodash-es/get'
  import findIndex from 'lodash-es/findIndex'
  import ScrollUtils from 'utils/scrollUtils'
  import Clickoutside from 'element-ui/src/utils/clickoutside'
  import ModalHeaderWhite from 'components/Modal/ModalHeaderWhite'
  import OrigDestUtils from 'store/utils/eHorizonOrigDestUtils'

  export default {
    directives: {
      Clickoutside
    },
    components: {
      ModalHeaderWhite
    },
    props: {
      error: {
        type: Boolean,
        required: false,
        default: false
      },
      placeholder: {
        type: String,
        required: false,
        default: ''
      },
      startsWith: {
        type: Boolean,
        required: false,
        default: false
      },
      items: {
        type: Array,
        default: () => []
      },
      selectedItem: {
        type: String,
        default: ''
      },
      mobileTitle: {
        type: String,
        default: '',
        required: false
      },
      splitTitle: {
        type: Boolean,
        default: false
      },
      ignoreListChangeEvent: {
        type: Boolean,
        default: false
      },
      clearOnFocus: {
        type: Boolean,
        default: false
      },
      needDestinationFilter: {
        type: Boolean,
        default: false
      },
      dropdownAutocomplete: {
        type: String,
        default: 'off'
      },
      showFlags: {
        type: Boolean,
        default: false
      },
      openInModal: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        selected: '',
        isOpened: false,
        results: [],
        searchString: '',
        lastAction: ''
      }
    },
    computed: {
      ...mapGetters('device', ['isMobile']),
      title() {
        const obj = find(this.items, ['value', this.selected])
        let title = get(obj, 'title', '')

        if (this.splitTitle) {
          const value = get(obj, 'value', '')
          title = value.split('|')[0]
        }
        if (title === '--') {
          title = ''
        }

        return title
      },
      emptyClass() {
        return this.results.length === 0 ? 'empty' : ''
      },
      search: {
        get() {
          return this.searchString
        },
        set(value) {
          this.searchString = value
        }
      }
    },
    watch: {
      isOpened(val) {
        if (!val) {
          this.searchString = this.title.toLowerCase()
        }
      },
      selectedItem(val) {
        this.selected = val
        this.search = this.title
      },
      items: {
        handler() {
          if (this.ignoreListChangeEvent) {
            this.selected = this.selectedItem
            this.search = this.title
          } else {
            this.selectItem(this.selectedItem)
          }
        },
        immediate: true
      }
    },
    mounted() {
      this.searchString = this.title.toLowerCase()
      this.results = this.items
      this.selected = this.selectedItem
    },
    methods: {
      selectOnEnter(next = false) {
        if (!this.isOpened) {
          this.isOpened = true
          return
        }

        const total = this.results.length

        if (total > 0 && this.lastAction !== 'select') {
          const item = this.results[0]

          if (item) {
            this.selectItem(item.value, true)
            this.scrollToElement(next)
          }
        } else {
          this.searchString = ''
          this.results = this.getFiltered()
        }
        this.toggleClosed()
      },

      selectNext(next = false) {
        if (!this.isOpened) {
          this.isOpened = true
        }

        this.lastAction = 'select'
        const index = findIndex(this.results, ['value', this.selected])
        const total = this.results.length
        let pos = null

        if (next && index < total) {
          pos = index - 1
        } else if (!next && index >= 0) {
          pos = index + 1
        }

        if (pos >= 0 && pos < total) {
          let item = this.results[`${pos}`]
          if (item == null) {
            item = this.results[0]
          }
          this.selectItem(item.value, true)
        }
        this.scrollToElement(next)
      },
      scrollToElement(next = false) {
        const activeDropdown = this.$el.getElementsByClassName('dropdown-list')[0]
        const dropdownElement = this.$el.querySelector('.dropdown-list')
        const dropdownHeight = this.isMobile
          ? window
              .getComputedStyle(dropdownElement)
              .getPropertyValue('padding-top')
              .replace(/\D/g, '')
          : 0
        const activeElement = activeDropdown.getElementsByClassName('selected')[0]

        if (activeDropdown && activeElement) {
          let scrollTo = activeElement.offsetTop

          if (next) {
            scrollTo = activeElement.offsetTop - activeElement.offsetHeight * 2
          }

          activeDropdown.scrollTop = scrollTo - dropdownHeight
        }
      },
      toggleDropdown() {
        if (!this.isOpened) {
          this.toggleOpened()
        } else {
          this.toggleClosed()
          this.lastAction = ''
        }
      },
      toggleOpened() {
        if (this.clearOnFocus && !this.isOpened) {
          this.searchString = ''
        }

        this.isOpened = true

        this.$nextTick(() => {
          if (this.isMobile && this.openInModal) {
            ScrollUtils.disableScroll()
            document.body.scrollTop = 0
            document.documentElement.scrollTop = 0
          } else {
            this.scrollToElement()
          }
        })
      },
      toggleClosed() {
        if (this.isMobile && this.openInModal) {
          ScrollUtils.enableScroll()
        }
        this.isOpened = false
        this.lastAction = ''
      },
      getFiltered() {
        return this.items.filter((item) => {
          const singleItem = String(item.title).toLowerCase()
          const search = String(this.searchString).toLowerCase()
          return !this.startsWith
            ? singleItem.indexOf(search) > -1
            : singleItem.startsWith(search)
        })
      },

      destinationFilter() {
        const search = String(this.searchString).toLowerCase()
        const ignoreFilter = search.length <= 0

        return OrigDestUtils.filterData(search, this.items, ignoreFilter)
      },

      onChange($e) {
        this.searchString = $e.target.value
        this.lastAction = 'filter'
        const activeDropdown = this.$el.getElementsByClassName('dropdown-list')[0]

        if (!this.isOpened) {
          this.isOpened = true
        }

        if (this.needDestinationFilter) {
          this.results = this.destinationFilter()

          this.$nextTick(() => {
            activeDropdown.scrollTop = 0
          })

          return
        }

        this.results = this.getFiltered()
      },
      close() {
        this.results = this.items
        this.isOpened = false
      },
      selectItem(item, arrowChange = false) {
        if (this.isOpened && !arrowChange) {
          this.close()
        }

        this.selected = item
        this.search = this.title
        this.$emit('select', item)
      },
      getFlagClass(key) {
        if (key) {
          return `flag-${key}`
        }
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';
  @import '~assets/styles/components/dropdown.scss';
  @import '~assets/styles/flag_sprite.scss';

  .dropdown-wrap {
    .arrow-down:after {
      content: $icon-arrow-right-thin;
      @include icon-font-thin();
      position: absolute;
      right: 19px;
      transform: rotate(90deg);
      font-weight: 700;
      line-height: 1;
      top: 20px;
      font-size: 11px;
      color: $gray-brown;
      cursor: pointer;

      @include bp-mf-tablet {
        top: 19px;
      }
    }

    &.open {
      .arrow-down:after {
        transform: rotate(-90deg);
        color: $blue-dark;
      }

      .dropdown-field {
        border: 1px solid $blue-dark;
      }

      .dropdown-list {
        height: auto;
        max-height: 240px;
        border: 1px solid $formInputBorder;
        border-top: none;
        box-shadow: 0 2px 4px 0 rgba($grey-box-shadow, 0.5);

        &.empty {
          height: 100%;
          min-height: 100%;

          @include bp-mf-tablet {
            height: 0;
            min-height: 0;
          }
        }
      }

      &.not-in-modal {
        .dropdown-field {
          position: static;
        }

        .dropdown-list {
          position: absolute;
          height: 240px;
          top: 48px;
        }

        .selected:after {
          content: none !important;
        }

        li {
          min-height: 48px;
        }
      }

      &:not(.not-in-modal) {
        .dropdown-field {
          @include bp-mobile {
            top: 54px;
            z-index: 999;
            position: fixed;
            left: 0;
            border-left: transparent;
            border-right: transparent;
          }
        }

        .dropdown-list {
          @include bp-mobile {
            top: 43px;
            height: calc(100% - 40px);
            max-height: none;
          }
        }
      }
    }

    .dropdown-list {
      padding: 4px;

      li {
        line-height: 48px;
        padding: 0 12px;
      }

      .selected {
        background-color: #ebeef5;
        border-radius: 4px;
      }
    }

    &.has-flags {
      .dropdown-field {
        &.has-flag {
          padding-left: 50px;
        }
      }
    }

    .flag,
    .input-flag {
      display: inline-block;
      width: 23px;
      height: 18px;
      margin-right: 16px;
      vertical-align: text-top;
    }

    .input-flag {
      position: absolute;
      top: 14px;
      left: 10px;
    }
  }
</style>
